// Pro identity + per-Pro location membership.
//
// A Pro can hold suites at multiple IMAGE Studios locations
// (e.g., Jessica Romano: Demo + Brookline). Every Hub page that
// shows tenant-scoped data must query against the *active* location,
// not assume a single tenant exists. Source these hooks in concert
// with `useActiveLocationId` from locationContext.
//
//   1. usePro()           — single row from `pros`, keyed by
//                           auth_user_id. Stable across location
//                           switches.
//   2. useProLocations()  — array of pro_locations the signed-in Pro
//                           belongs to, with the joined display name
//                           and suite number for selector UX.
//                           Sorted by tenant updated_at desc (most-
//                           recent first) so the default-pick logic
//                           in LocationProvider lands on the location
//                           the Pro most recently used.
//
// RLS does the access enforcement: pro_locations + pros are gated by
// `app_my_pro_id()` so authenticated reads naturally return only the
// signed-in Pro's rows. Single-location Pros get a 1-element array
// and the selector hides itself.

import { useQuery } from '@tanstack/react-query';
import { useSession } from './auth';
import { supabase } from './supabase';
import { isCell } from './cell/config';
import { cellGet } from './cell/api';

export interface ProIdentity {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export interface ProLocation {
  location_id: string;
  display_name: string;
  /** Tenant row id at this location. Empty string if pro_locations
   *  has no current_tenant_id link (data drift; selector still works,
   *  just no suite number displayed). */
  tenant_id: string;
  suite_number: number | null;
  salon_name: string | null;
  is_primary: boolean;
  /** ISO date from tenants.updated_at. Drives most-recent default. */
  updated_at: string;
}

export function usePro() {
  const { session } = useSession();
  const userId = session?.user?.id;

  return useQuery<ProIdentity | null>({
    queryKey: ['hub-pro', userId],
    enabled: !!userId,
    queryFn: async () => {
      if (isCell()) { // CELL (M28): GET /v1/pro-hub/users/{authUserId}/pro
        const { data: r } = await cellGet<{ pro: ProIdentity | null }>(`/v1/pro-hub/users/${userId}/pro`);
        return r?.pro ?? null;
      }
      const { data } = await supabase
        .from('pros')
        .select('id, first_name, last_name, email')
        .eq('auth_user_id', userId!)
        .maybeSingle();
      return (data as ProIdentity | null) ?? null;
    },
  });
}

export function useProLocations() {
  const { data: pro } = usePro();
  const proId = pro?.id;

  return useQuery<ProLocation[]>({
    queryKey: ['hub-pro-locations', proId],
    enabled: !!proId,
    queryFn: async () => {
      // TWO QUERIES, NOT ONE EMBED (fixed 2026-08-20). The old select embedded
      // `location_configs!location_id` — and PostgREST answered PGRST200 ("no
      // relationship between pro_locations and location_configs") on EVERY call,
      // because no such foreign key exists for it to walk. The `return []` below
      // then swallowed that error, so every pro silently had zero locations:
      // activeLocationId never resolved, the studio name/events/tickets on Home
      // never populated, the hero tag fell back to "IMAGE STUDIOS", and the
      // selector hid — all reading as "working" because downstream pages carry
      // most-recent-tenant fallbacks. Found only because the spotlight gate's
      // live verification needed the location to resolve. The catch-{} disease,
      // hub edition: a loader that returns [] on error hides its own outage.
      // CELL (M28): GET /v1/pro-hub/pros/{proId}/locations — both reads below, one endpoint, the same rows.
      const cell = isCell() ? await cellGet<{ memberships: unknown[]; names: Array<{ location_id: string; value: string | null }> }>(`/v1/pro-hub/pros/${proId}/locations`) : null;
      const { data, error } = cell ? { data: cell.data?.memberships ?? null, error: cell.error } : await supabase
        .from('pro_locations')
        .select(`
          location_id,
          is_primary,
          current_tenant_id,
          tenants!current_tenant_id (
            id,
            salon_name,
            updated_at,
            suites!suite_id (suite_number)
          )
        `)
        .eq('pro_id', proId!)
        // CURRENT memberships only. pro_locations keeps a row after a pro leaves a studio
        // (end_date is set, the row is never deleted — see migration 20260731000000), so
        // without this filter the switcher offers studios the pro has left and every page
        // downstream can resolve to one. Retiring sharon_ma on 2026-08-07 closed 18 such
        // rows; before that they read as live.
        .or(`end_date.is.null,end_date.gte.${new Date().toISOString().slice(0, 10)}`);

      if (error) {
        // Say it. An empty membership list from an ERROR must not impersonate a
        // pro who genuinely has none.
        console.error('pro_locations read failed', error);
        return [];
      }
      if (!data) return [];

      // Display names come in a second, plain read — location_configs is keyed by
      // location_id but carries no FK PostgREST can traverse from pro_locations.
      const ids = (data as Array<{ location_id: string }>).map((r) => r.location_id);
      const nameById = new Map<string, string>();
      if (ids.length) {
        // THE NAME AN OWNER TYPED, from My Studio (Peter, 2026-08-26) — the
        // studio picker showed location_configs.display_name, a copy no owner
        // edits. See lib/studioFacts.ts.
        // CELL (M28): the names came with the memberships (same read, same filters).
        const { data: kb, error: kbErr } = cell ? { data: cell.data?.names ?? [], error: null } : await supabase
          .from('location_kb_values')
          .select('location_id, value')
          .eq('ns', 'location')
          .eq('key', 'display_name')
          .in('location_id', ids);
        if (kbErr) console.error('My Studio name read failed', kbErr);
        for (const c of (kb ?? []) as Array<{ location_id: string; value: string | null }>) {
          const name = (c.value ?? '').trim();
          if (name) nameById.set(c.location_id, name);
        }
      }

      type Row = {
        location_id: string;
        is_primary: boolean | null;
        current_tenant_id: string | null;
        tenants: {
          id: string;
          salon_name: string | null;
          updated_at: string | null;
          suites: { suite_number: number | null } | null;
        } | null;
      };

      const rows = (data as unknown as Row[]).map((r): ProLocation => ({
        location_id: r.location_id,
        display_name: nameById.get(r.location_id) ?? r.location_id,
        tenant_id: r.tenants?.id ?? '',
        suite_number: r.tenants?.suites?.suite_number ?? null,
        salon_name: r.tenants?.salon_name ?? null,
        is_primary: r.is_primary === true,
        updated_at: r.tenants?.updated_at ?? '1970-01-01T00:00:00Z',
      }));

      // Most-recently-used first; LocationProvider picks rows[0] as default.
      return rows.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    },
  });
}
